import { useState, useEffect } from 'react'
import {
  Avatar,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Text,
  Modal,
  HStack,
  VStack,
  Tag,
  Box
} from '@chakra-ui/react'
import dayjs from 'dayjs'
import 'dayjs/locale/en'
import advancedFormat from 'dayjs/plugin/advancedFormat'
import { useStores } from 'hooks/useStores'
import { observer } from 'mobx-react'
import { S3FileTypeEnum } from 'constants/aws'
import { EIconDefaultId } from 'interfaces/iconBuilder'
import { IWorkPlanWithRelations } from 'interfaces/plan'
import { ITheme } from 'interfaces/theme'
import IconBuilder from 'pages/IconBuilderPage/components/IconBuilder'
import { getS3FileUrl } from 'utils/images'
import { formatTaktTime } from 'utils/formatTime'
import { EStatus } from 'constants/workplan'
import StateBadge from 'components/StateBadge'

interface IPlanSummaryModalProps {
  isOpen: boolean
  onClose: () => void
  selectedPlan?: IWorkPlanWithRelations
}

const PlanSummary = (props: IPlanSummaryModalProps) => {
  const { isOpen, onClose, selectedPlan } = props
  const { iconBuilderStore, organizationStore } = useStores()
  const { organization } = organizationStore
  const currentTheme: ITheme = organization?.theme ?? {}

  dayjs.extend(advancedFormat)
  dayjs.locale('en')

  const getStateBadgeColor = (status: number) => {
    return status === 1 ? 'purple' : 'yellow'
  }

  const getStateBadgeTextColor = (status: number) => {
    return status === 1 ? 'white' : 'black'
  }

  const formatDate = (dateString: string) => {
    return dayjs(dateString).format('MMMM Do, YYYY')
  }

  if (!selectedPlan) return null

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader fontSize="18px" lineHeight={7} color="gray.800" fontWeight={500} borderBottom="1px solid #E2E8F0">
          Work Plan Summary
        </ModalHeader>
        <ModalCloseButton
          boxShadow="unset"
          border="unset"
          background="#fff"
          _focus={{ background: '#fff', borderColor: 'unset' }}
          _active={{ background: '#fff', borderColor: 'unset' }}
        />
        <ModalBody padding={6}>
          <VStack spacing={6} alignItems="flex-start">
            <HStack spacing={4} alignItems="center">
              <IconBuilder icon={iconBuilderStore.getIconById(EIconDefaultId.DOCUMENT_TYPE)} size={40} isActive />
              <VStack spacing={1} alignItems="flex-start">
                <Text fontSize="18px" fontWeight={600} color="gray.800">
                  {selectedPlan.name}
                </Text>
                <StateBadge status={EStatus.ACTIVE} />
              </VStack>
            </HStack>

            <VStack spacing={4} alignItems="flex-start" width="full">
              <HStack spacing={4} alignItems="flex-start" width="full">
                <Text fontWeight={600} fontSize="16px" color="gray.700" minWidth="120px">
                  Descriptions:
                </Text>
                <Text fontSize="16px" color="gray.600" flex={1}>
                  {selectedPlan.description || 'No description available'}
                </Text>
              </HStack>

              <HStack spacing={4} alignItems="center" width="full">
                <Text fontWeight={600} fontSize="16px" color="gray.700" minWidth="120px">
                  Created By:
                </Text>
                <HStack spacing={2}>
                  <Avatar
                    size="sm"
                    name={selectedPlan.creatorName || 'Unknown User'}
                    src={
                      selectedPlan.creatorImage
                        ? getS3FileUrl(
                            S3FileTypeEnum.IMAGE,
                            selectedPlan.organizationId || 0,
                            selectedPlan.creatorImage
                          )
                        : undefined
                    }
                  />
                  <Text fontSize="16px" color="gray.600">
                    {selectedPlan.creatorName || 'Unknown User'}
                  </Text>
                </HStack>
              </HStack>

              <HStack spacing={4} alignItems="center" width="full">
                <Text fontWeight={600} fontSize="16px" color="gray.700" minWidth="120px">
                  Duration:
                </Text>
                <Text fontSize="16px" color="gray.600">
                  {formatTaktTime(selectedPlan.taktTime)}
                </Text>
              </HStack>

              <HStack spacing={4} alignItems="center" width="full">
                <Text fontWeight={600} fontSize="16px" color="gray.700" minWidth="120px">
                  Number of Stations:
                </Text>
                <Text fontSize="16px" color="gray.600">
                  {selectedPlan.workStationCount || 0}
                </Text>
              </HStack>

              <HStack spacing={4} alignItems="center" width="full">
                <Text fontWeight={600} fontSize="16px" color="gray.700" minWidth="120px">
                  Last Updated:
                </Text>
                <Text fontSize="16px" color="gray.600">
                  {formatDate(selectedPlan.updatedAt)}
                </Text>
              </HStack>

              {selectedPlan.status === 1 && (
                <HStack spacing={4} alignItems="center" width="full">
                  <Text fontWeight={600} fontSize="16px" color="gray.700" minWidth="120px">
                    Number of drafts:
                  </Text>
                  <Text fontSize="16px" color="gray.600">
                    1
                  </Text>
                </HStack>
              )}
            </VStack>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

export default observer(PlanSummary)
